import { BILLING_BRAND } from '../data/billingBrandConfig.js';
import {
  ONBOARDING_EVENT,
  ONBOARDING_STORAGE_KEY,
  registerOnboardingTours,
  getOnboardingTour,
  readOnboardingState,
  writeOnboardingState,
  startOnboardingTour,
  resolveOnboardingStepIndex,
  pauseOnboardingTour,
  resumeOnboardingTour,
  completeOnboardingTour,
  cancelOnboardingTour,
  patchOnboardingContext,
  readPausedOnboardingTour,
  maybeTrackOnboardingAbandoned,
  hydrateOnboardingTour,
  getOnboardingRoute,
  migrateLegacyOnboardingState,
  trackOnboardingEngineEvent,
} from './onboardingEngine.js';

/**
 * Recorrido guiado de primera venta.
 *
 * La máquina de estado vive ahora en `onboardingEngine` — la misma que usan los
 * tours de puesta a punto y los de módulo. Este archivo quedó con lo que de
 * verdad es propio de cada app: el CONTENIDO del recorrido (pasos, textos,
 * selectores) y una fachada con la API histórica, para no reescribir los
 * consumidores (`Sistema.jsx` y `Dashboard.jsx` están PROTECTED y divergen).
 *
 * Impulza y Gastify comparten motor y divergen en contenido: Gastify tiene el
 * recorrido corto de 7 tramos (producto → venta → rentabilidad → puesta a
 * punto) e Impulza el suyo.
 */

// Un único tour registrado en el motor. El id viaja a `onboarding_events` y a
// `onboarding_progress`, así que no se renombra a la ligera.
export const FIRST_SALE_TOUR_ID = 'first-sale-demo';

// El estado unificado vive en la clave del motor. Se conservan los nombres
// históricos porque los importan las pantallas.
export const FIRST_SALE_ONBOARDING_STORAGE_KEY = ONBOARDING_STORAGE_KEY;
export const FIRST_SALE_ONBOARDING_EVENT = ONBOARDING_EVENT;

const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';

// Tour corto (H3): producto → venta → rentabilidad → puesta a punto.
// Caja, proveedores y compras salieron del recorrido inicial: se aprenden
// después, bajo demanda, desde el checklist "Empezá a operar"
// (setupOnboarding.js + setupOnboardingTours.js). La creación de producto y la
// venta del recorrido son SIMULADAS por las páginas (no escriben en la base).
export const FIRST_SALE_ONBOARDING_STEPS = [
  'welcome-demo',
  'conocer-academia',
  'conocer-progreso',
  'elegir-camino',
];

// El recorrido ya no navega por el sistema creando datos simulados: los tres
// primeros tramos viven en el dashboard y el ultimo espera al usuario en
// "Empeza a operar", que es a donde lo lleva su propio clic en el paso
// anterior. Si el ultimo tramo siguiera apuntando al dashboard, completar
// 'conocer-progreso' lo sacaria de /sistema/progreso justo despues de entrar.
const FIRST_SALE_ROUTE_MAP = {
  'welcome-demo': '/sistema/dashboard',
  'conocer-academia': '/sistema/dashboard',
  'conocer-progreso': '/sistema/dashboard',
  'elegir-camino': '/sistema/progreso',
};

// Traducción de ids externos → pasos vigentes.
// 1) Las páginas que avanzan pasos (ProductoNuevo, VentasPOS,
//    RentabilidadVentas) viajan por sync desde Impulza y emiten los ids del
//    tour de Impulza (p. ej. 'visit-dashboard'). Sin este mapa, ese avance era
//    un no-op silencioso (diagnóstico H8).
// 2) Los ids del recorrido largo anterior pueden seguir persistidos en el
//    estado de usuarios a mitad de tour: se migran al paso equivalente en vez
//    de descartar el recorrido.
const STEP_ALIASES = {
  // Todos los ids del recorrido largo anterior caen en 'conocer-progreso': un
  // usuario que quedo a mitad del tour viejo retoma en la explicacion de
  // "Empeza a operar" en vez de perder el recorrido o volver a empezar.
  'visit-dashboard': 'conocer-progreso',
  'nav-to-academia': 'conocer-academia',
  'nav-to-productos': 'conocer-progreso',
  'create-product': 'conocer-progreso',
  'sell-product': 'conocer-progreso',
  'review-profitability': 'conocer-progreso',
  'review-dashboard': 'conocer-progreso',
  'nav-to-progreso': 'conocer-progreso',
  // El que ya habia llegado al final del tour viejo cae directo en la eleccion.
  'visit-progreso': 'elegir-camino',
  // — ids legacy del recorrido largo de Gastify —
  'nav-to-caja': 'conocer-progreso',
  'open-caja': 'conocer-progreso',
  'nav-to-proveedores': 'conocer-progreso',
  'create-provider': 'conocer-progreso',
  'nav-to-compras': 'conocer-progreso',
  'buy-product': 'conocer-progreso',
  'nav-to-pos': 'conocer-progreso',
  'nav-to-rentabilidad': 'conocer-progreso',
};

registerOnboardingTours([{
  id: FIRST_SALE_TOUR_ID,
  kind: 'demo',
  title: `Primeros pasos en ${BRAND_NAME}`,
  badge: 'Recorrido guiado',
  // Se persiste server-side: el recorrido se retoma desde otro dispositivo.
  persist: true,
  stepAliases: STEP_ALIASES,
  // Cada "paso" del motor es un tramo del recorrido; el detalle de coach-marks
  // de cada tramo lo arma buildFirstSaleOnboardingDefinition con el contexto
  // (producto creado, venta simulada), que cambia en tiempo de ejecución.
  steps: FIRST_SALE_ONBOARDING_STEPS.map((stepId) => ({
    id: stepId,
    route: FIRST_SALE_ROUTE_MAP[stepId],
  })),
}]);

const getTour = () => getOnboardingTour(FIRST_SALE_TOUR_ID);

const sanitizeSaleData = (sale) => {
  if (!sale || typeof sale !== 'object') return null;

  return {
    id: sale.id || null,
    date: sale.date || null,
    total: Number(sale.total || 0),
    paymentMethodId: sale.paymentMethodId || null,
    paymentMethodName: sale.paymentMethodName || '',
    productId: sale.productId || null,
    productName: sale.productName || '',
    quantity: Number(sale.quantity || 0),
    unitPrice: Number(sale.unitPrice || 0),
    revenue: Number(sale.revenue || 0),
    cost: Number(sale.cost || 0),
    profit: Number(sale.profit || 0),
    marginPct: Number(sale.marginPct || 0),
  };
};

const sanitizeProductData = (product) => {
  if (!product || typeof product !== 'object') return null;

  return {
    id: product.id || null,
    name: product.name || '',
    costPrice: Number(product.costPrice || 0),
    sellPrice: Number(product.sellPrice || 0),
  };
};

/**
 * Adapta el estado del motor a la forma histórica `{stepId, product, sale, …}`
 * que consumen las pantallas. La traducción vive acá y no en el motor: el motor
 * no sabe de "productos" ni de "ventas".
 */
const toLegacyState = (state) => {
  if (!state || state.tourId !== FIRST_SALE_TOUR_ID) return null;
  const tour = getTour();
  const stepId = tour?.steps?.[state.stepIndex]?.id;
  if (!stepId) return null;

  return {
    version: 1,
    status: state.status,
    organizationId: state.organizationId,
    userId: state.userId,
    businessType: state.businessType,
    stepId,
    startedAt: state.startedAt,
    updatedAt: state.updatedAt,
    abandonedLoggedAt: state.abandonedLoggedAt,
    product: sanitizeProductData(state.context?.product),
    sale: sanitizeSaleData(state.context?.sale),
  };
};

export function readFirstSaleOnboarding() {
  return toLegacyState(readOnboardingState());
}

export function writeFirstSaleOnboarding(state) {
  if (!state) return cancelOnboardingTour();

  const index = resolveOnboardingStepIndex(getTour(), state.stepId);
  if (index < 0) return null;

  return toLegacyState(writeOnboardingState({
    tourId: FIRST_SALE_TOUR_ID,
    stepIndex: index,
    status: state.status,
    organizationId: state.organizationId,
    userId: state.userId,
    businessType: state.businessType,
    startedAt: state.startedAt,
    updatedAt: state.updatedAt,
    abandonedLoggedAt: state.abandonedLoggedAt,
    context: {
      product: sanitizeProductData(state.product),
      sale: sanitizeSaleData(state.sale),
    },
  }));
}

export function startFirstSaleOnboarding({
  organizationId,
  userId,
  businessType = null,
}) {
  if (!organizationId) return null;

  // El motor emite `tour_started` con stepId 'welcome-demo': mismo evento y
  // mismo id que antes, así el funnel histórico sigue siendo comparable.
  return toLegacyState(startOnboardingTour({
    tourId: FIRST_SALE_TOUR_ID,
    organizationId,
    userId: userId || null,
    businessType: businessType || null,
    context: { product: null, sale: null },
    // Arranque limpio: descarta cualquier guía abierta para que no reaparezca
    // al terminar el recorrido.
    fresh: true,
  }));
}

export function patchFirstSaleOnboarding(patch = {}) {
  const current = readOnboardingState();
  if (!current || current.tourId !== FIRST_SALE_TOUR_ID) return null;

  const { stepId, product, sale, ...rest } = patch;
  const context = { ...current.context };
  if (product !== undefined) context.product = sanitizeProductData(product);
  if (sale !== undefined) context.sale = sanitizeSaleData(sale);

  const stepIndex = stepId !== undefined
    ? resolveOnboardingStepIndex(getTour(), stepId)
    : current.stepIndex;
  if (stepIndex < 0) return null;

  return toLegacyState(writeOnboardingState({
    ...current,
    ...rest,
    stepIndex,
    updatedAt: new Date().toISOString(),
    context,
  }));
}

export function goToFirstSaleOnboardingStep(stepId, patch = {}) {
  const tour = getTour();
  const index = resolveOnboardingStepIndex(tour, stepId);
  if (index < 0) return null;
  const resolvedStepId = tour.steps[index].id;

  const next = patchFirstSaleOnboarding({ ...patch, stepId: resolvedStepId, status: 'active' });
  if (!next) return null;

  // El evento reporta el paso al que se LLEGA (semántica histórica del
  // recorrido): cambiarla partiría en dos la serie de `onboarding_events`.
  trackOnboardingEngineEvent({
    event: 'tour_step_completed',
    stepId: resolvedStepId,
    organizationId: next.organizationId,
    userId: next.userId,
    metadata: { tourId: FIRST_SALE_TOUR_ID },
  });

  // Antes acá se emitía `first_real_sale` al llegar al paso de rentabilidad.
  // Era el ÚNICO emisor del evento y la venta siempre venía simulada por el
  // POS del recorrido, así que el embudo contaba demos como activaciones. El
  // recorrido ya no simula ventas, y la activación real se mide donde vive el
  // dato: la primera fila de `sales` de la organización (ver la vista
  // onboarding_funnel_weekly y superadmin_altas.primera_venta_at).

  return next;
}

export function completeFirstSaleOnboarding() {
  return toLegacyState(completeOnboardingTour());
}

// El usuario sale del recorrido pero puede retomarlo después (1.3/F4).
export function pauseFirstSaleOnboarding() {
  return toLegacyState(pauseOnboardingTour());
}

export function resumeFirstSaleOnboarding() {
  return toLegacyState(resumeOnboardingTour());
}

// Un recorrido pausado hace más de N días es un abandono de facto. Se emite
// `tour_abandoned` una sola vez; el estado sigue 'paused' y retomable.
export function maybeTrackTourAbandoned(state) {
  const current = readOnboardingState();
  if (!current) return state;
  return toLegacyState(maybeTrackOnboardingAbandoned(current));
}

// Estado pausado recuperable para el CTA "Retomar recorrido" del dashboard.
export function readPausedFirstSaleOnboarding({ organizationId, userId } = {}) {
  return toLegacyState(readPausedOnboardingTour({ organizationId, userId }));
}

export function clearFirstSaleOnboarding() {
  return cancelOnboardingTour();
}

/**
 * Hidrata el cache local al iniciar sesión. Hace dos cosas, en este orden:
 *
 * 1. Migra un recorrido en curso de los motores anteriores. Quien estaba a
 *    mitad de camino cuando salió este deploy sigue donde estaba, en vez de
 *    perder el progreso (y con él, probablemente, el trial).
 * 2. Trae el progreso desde `onboarding_progress` para retomar el recorrido en
 *    otro dispositivo o navegador.
 */
export async function hydrateFirstSaleOnboarding({ organizationId, userId } = {}) {
  migrateLegacyOnboardingState({ demoTourId: FIRST_SALE_TOUR_ID });

  return toLegacyState(await hydrateOnboardingTour({
    organizationId,
    userId,
    tourId: FIRST_SALE_TOUR_ID,
  }));
}

// Evalúa el estado que recibe (el que la pantalla tiene en su render), no el
// vigente del motor: `Sistema.jsx` decide con esto si renderiza el recorrido.
export function matchesFirstSaleOnboardingIdentity(state, {
  organizationId,
  userId,
} = {}) {
  if (!state || state.status !== 'active') return false;
  if (organizationId && state.organizationId !== organizationId) return false;
  if (userId && state.userId && state.userId !== userId) return false;
  return true;
}

export function getFirstSaleOnboardingRoute(stepId) {
  if (stepId === undefined) return getOnboardingRoute(readOnboardingState());
  const index = resolveOnboardingStepIndex(getTour(), stepId);
  if (index < 0) return '/sistema/dashboard';
  return FIRST_SALE_ROUTE_MAP[getTour().steps[index].id] || '/sistema/dashboard';
}

// Guarda datos del recorrido (producto creado, venta simulada) sin mover el
// paso. Expuesto para las páginas que simulan la operación.
export function patchFirstSaleOnboardingContext(patch = {}) {
  return toLegacyState(patchOnboardingContext(patch));
}

export function buildFirstSaleOnboardingDefinition(stepId, context = {}) {
  if (stepId === 'welcome-demo') {
    // Una linea de bienvenida segun el rubro elegido en el alta. El detalle de
    // cada vertical se completa despues, en el checklist "Empeza a operar".
    const bt = String(context.businessType || '').toLowerCase();
    const WELCOME_INTRO_BY_TYPE = {
      gastronomia: 'Pensado para gastronomía: más adelante vas a cargar recetas y ver el costo de cada plato.',
      servicios: 'Pensado para servicios: más adelante vas a armar presupuestos y facturar sin stock.',
      indumentaria: 'Pensado para indumentaria: vas a poder vender por talle y color desde el POS.',
    };
    const intro = WELCOME_INTRO_BY_TYPE[bt]
      ? `${WELCOME_INTRO_BY_TYPE[bt]} Arranquemos ubicándote en la pantalla.`
      : 'Arranquemos ubicándote en la pantalla: dónde está cada cosa y cómo moverte.';
    return {
      id: stepId,
      title: `Te damos la bienvenida a ${BRAND_NAME}`,
      badge: 'Recorrido guiado',
      // Tramo intermedio: se encadena con el siguiente, no cierra nada. El
      // festejo del recorrido vive una sola vez, en 'elegir-camino'.
      celebrateOnComplete: false,
      description: 'Un minuto para ubicarte. Después te muestro dónde está la lista de puesta a punto y elegís cómo seguir.',
      steps: [
        {
          title: `¡Te damos la bienvenida a ${BRAND_NAME}!`,
          description: intro,
        },
        {
          selector: '[data-onboarding-id="topbar-site-selector"]',
          placement: 'bottom',
          title: 'Selector de sucursales y usuario',
          description: 'Acá arriba podés alternar entre tus sucursales, ver cajas independientes o acceder a la configuración de tu cuenta.',
        },
        {
          selector: '[data-onboarding-id="topbar-menu-items-group"]',
          placement: 'bottom',
          title: 'Menú de módulos (Ventas, Stock, Compras...)',
          description: 'En esta barra superior tenés acceso directo a todos los módulos: Ventas, Clientes, Stock, Compras, Finanzas y Reportes.',
        },
        {
          selector: '[data-onboarding-id="dashboard-content-area"]',
          placement: 'top',
          title: 'Área de trabajo',
          description: 'Acá abajo se despliega el contenido de la pantalla activa. Ahora ves tu Dashboard, con el resumen diario de facturación, caja y alertas.',
        },
        {
          title: 'Eso es todo lo que necesitás para moverte',
          description: 'Con esos tres lugares ya podés navegar el sistema entero. Ahora te muestro dónde está la lista de lo que falta para dejar tu negocio listo para vender.',
        },
      ],
    };
  }

  if (stepId === 'conocer-academia') {
    return {
      id: stepId,
      title: 'La Academia, para cuando quieras profundizar',
      badge: 'Academia',
      // Este tramo solo muestra donde vive el boton de cursos. Festejar aca
      // era felicitar al usuario por una mision que no hizo.
      celebrateOnComplete: false,
      description: 'Cursos cortos por módulo, a tu ritmo y cuando los necesites.',
      steps: [
        {
          selector: '[data-onboarding-id="topbar-academia"]',
          placement: 'bottom',
          title: 'Acá vive la Academia',
          description: 'Este botón te abre los cursos: cómo cargar productos, cómo cobrar en el POS, cómo cerrar la caja. Cada uno dura pocos minutos.',
          detail: 'No hace falta verlos para operar. Está ahí para cuando quieras entender un módulo a fondo o sumar a alguien del equipo.',
        },
      ],
    };
  }

  if (stepId === 'conocer-progreso') {
    return {
      id: stepId,
      title: '"Empezá a operar": tu lista de puesta a punto',
      badge: 'Puesta a punto',
      // Tramo intermedio: lleva a /sistema/progreso y encadena con la eleccion.
      celebrateOnComplete: false,
      description: 'Ahí vive todo lo que falta para que tu negocio quede listo para vender de verdad.',
      steps: [
        {
          selector: '[data-onboarding-id="topbar-menu-item-progreso"]',
          placement: 'bottom',
          // navStep: el paso se cierra con el clic en el boton real, no con un
          // "Entendido" en el cartel. Ofrecer las dos salidas era peor que no
          // ofrecer ninguna: el usuario tocaba "Entendido", el recorrido
          // avanzaba y el se quedaba sin saber donde estaba la lista.
          navStep: true,
          // El tramo lo cierra la llegada a /sistema/progreso, que Sistema.jsx
          // detecta por ruta. No se depende del clic sobre el boton: el coach-mark
          // engancha su listener una sola vez y el boton "Empeza a operar" se
          // monta recien cuando termina de cargar el checklist, asi que muchas
          // veces no habia a quien escuchar y el paso no avanzaba nunca.
          hideAdvance: true,
          title: 'Tocá "Empezá a operar" acá arriba',
          description: 'Te lleva a la lista de puesta a punto: cargar tu catálogo, ingresar el stock que tenés, hacer tu primera venta y abrir la caja. El porcentaje sube solo a medida que avanzás.',
          detail: 'No hace falta que hagas todo hoy ni en orden. El botón queda siempre en la barra superior para volver cuando quieras.',
        },
      ],
    };
  }

  if (stepId === 'elegir-camino') {
    return {
      id: stepId,
      title: '¿Cómo querés seguir?',
      badge: 'Vos elegís',
      // Unico tramo que festeja: es el cierre real del recorrido de bienvenida
      // y ocurre ya dentro de /sistema/progreso. El texto por defecto habla de
      // una "mision de aprendizaje" y aca no se completo ninguna: se recorrio
      // el sistema.
      completeMessage: `🎉 ¡Listo! Ya sabés moverte por ${BRAND_NAME}.`,
      description: 'Dos caminos, y ninguno es obligatorio. Podés cambiar de idea cuando quieras.',
      steps: [
        {
          selector: '[data-onboarding-id="topbar-menu-item-progreso"]',
          placement: 'bottom',
          title: 'Con las guías, paso a paso',
          description: 'Entrá a "Empezá a operar" y tocá "Iniciar guía" en el primer paso: te acompaño en cada pantalla mientras cargás tus datos reales.',
          detail: 'Es el camino recomendado si es tu primera vez con un sistema de gestión.',
        },
        {
          title: 'O explorá por tu cuenta',
          description: 'Cerrá este recorrido y navegá libremente. La lista te espera en el botón de arriba, y podés reactivar este recorrido cuando quieras desde el "?" de la barra superior.',
        },
      ],
    };
  }

  return null;
}
